import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A ROW YOU CAN REACH THE END OF WITH A MOUSE.
 *
 * `.libe-strip` has always scrolled — `overflow-x: auto` with the scrollbar
 * hidden, because a scrollbar under every row on a television is furniture.
 * The cost of hiding it was that in a browser there was no affordance at all:
 * the row simply ended, and everything past the fifth poster was unreachable
 * unless you happened to try shift+wheel.
 *
 * So the arrows are the scrollbar's replacement, and they follow the same
 * rule: they exist only where a pointer does. On a television they are hidden
 * in CSS — the remote moves the cursor and the strip follows it, which is a
 * better answer than a button you would have to focus to press.
 *
 * EACH ARROW DISAPPEARS AT ITS END rather than greying out. A control that is
 * present but dead invites the press it cannot answer, and at the start of a
 * row "left" is not disabled so much as meaningless.
 */
export function Strip({ className, children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // A few pixels of slack: sub-pixel layout means scrollLeft rarely lands
    // exactly on zero or exactly on the maximum.
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setEnds((was) => (was.left === left && was.right === right ? was : { left, right }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    /* The row's contents arrive after the first paint — posters load, a query
       resolves — and each changes scrollWidth without firing a scroll. */
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure, children]);

  const nudge = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    /* Most of a screenful, not all of it: leaving a card visible from the
       previous view is what makes a paged row feel continuous rather than
       like a slideshow. */
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div className="strip-wrap">
      {ends.left && (
        <button
          type="button"
          className="strip-arrow is-left"
          aria-label="Scroll left"
          /* NOT focusable by the remote: this is pointer furniture, and a
             television walking its cursor into it would be a dead end. */
          tabIndex={-1}
          onClick={() => nudge(-1)}
        >
          ‹
        </button>
      )}

      <div ref={ref} className={className ? `libe-strip ${className}` : 'libe-strip'} onScroll={measure}>
        {children}
      </div>

      {ends.right && (
        <button
          type="button"
          className="strip-arrow is-right"
          aria-label="Scroll right"
          tabIndex={-1}
          onClick={() => nudge(1)}
        >
          ›
        </button>
      )}
    </div>
  );
}
